import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { API, inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Loader2, FileText, ExternalLink, Download } from "lucide-react";

const F = "bg-slate-800 border-white/10 text-white";

function BillingSettings() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/billing-settings").then(({ data }) => setB(data)); }, []);
  if (!b) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const set = (k, v) => setB({ ...b, [k]: v });
  const save = async () => { setSaving(true); try { const { data } = await api.put("/admin/billing-settings", { ...b, effective: undefined }); setB(data); toast.success("Billing settings saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); } };
  const eff = b.effective || {};
  const T = ({ k, label, ph, type = "text" }) => (<div><Label className="text-xs text-slate-400">{label}</Label><Input type={type} className={F} placeholder={ph} value={b[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`bill-${k}`} /></div>);
  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h3 className="text-white font-semibold text-sm mb-3">Legal / Billing Entity</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <T k="business_name" label="Business name" ph={eff.business_name} />
          <T k="legal_name" label="Legal business name" ph={eff.business_name} />
          <T k="email" label="Billing email" ph={eff.email} />
          <T k="phone" label="Billing phone" ph={eff.phone} />
          <T k="gstin" label="GSTIN" />
          <div className="sm:col-span-2"><T k="address" label="Billing address" ph={eff.address} /></div>
          <T k="city" label="City" /><T k="state" label="State" />
          <T k="pin_code" label="PIN" /><T k="country" label="Country" ph="India" />
        </div>
      </div>
      <div>
        <h3 className="text-white font-semibold text-sm mb-3">Invoice Configuration</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <T k="invoice_prefix" label="Invoice prefix" ph="WBG" />
          <T k="invoice_start" label="Starting number" type="number" />
          <T k="invoice_padding" label="Number padding" type="number" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <T k="payment_terms" label="Payment terms" />
          <T k="invoice_footer" label="Invoice footer note" />
        </div>
        <p className="text-xs text-slate-500 mt-2">Next invoices will look like <span className="text-teal-400 font-mono">{(b.invoice_prefix || "WBG")}-{String(b.invoice_start || 1).padStart(Number(b.invoice_padding) || 5, "0")}</span>. Issued invoice numbers never change.</p>
      </div>
      <Button className="bg-teal hover:bg-teal-dark text-white" onClick={save} disabled={saving} data-testid="billing-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save billing settings"}</Button>
    </div>
  );
}

function Invoices() {
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/admin/invoices")).data });
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const rows = data.filter((i) => !q || (i.invoice_no || "").toLowerCase().includes(q.toLowerCase()) || (i.order_no || "").toLowerCase().includes(q.toLowerCase()) || (i.email || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3">
      <Input className={F + " max-w-xs"} placeholder="Search invoice / order / email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inv-search" />
      <div className="bg-slate-900 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase"><tr><th className="text-left px-4 py-3">Invoice</th><th className="text-left px-4 py-3">Order</th><th className="text-left px-4 py-3">Customer</th><th className="text-right px-4 py-3">Total</th><th className="text-left px-4 py-3">Date</th><th className="text-right px-4 py-3">Action</th></tr></thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((i) => (
              <tr key={i.id} data-testid={`inv-row-${i.invoice_no}`}>
                <td className="px-4 py-3 font-mono text-xs text-teal-300">{i.invoice_no}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{i.order_no}</td>
                <td className="px-4 py-3 text-slate-300">{i.email}</td>
                <td className="px-4 py-3 text-right text-slate-200">{inr(i.total)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{(i.issued_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    <a href={`${API}/invoices/${i.id}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-teal-400 hover:text-teal-300 text-xs" data-testid={`inv-pdf-${i.invoice_no}`}>Download PDF <Download className="w-3 h-3" /></a>
                    <a href={`${API}/invoices/${i.id}/view`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-300 text-xs" data-testid={`inv-view-${i.invoice_no}`}>View <ExternalLink className="w-3 h-3" /></a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="p-4 text-slate-500 text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> No invoices yet. They are generated automatically when an order is paid.</p>}
      </div>
    </div>
  );
}

function BankDetails() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/bank-details").then(({ data }) => setB(data || {})); }, []);
  if (!b) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const set = (k, v) => setB({ ...b, [k]: v });
  const save = async () => { setSaving(true); try { const { data } = await api.put("/admin/bank-details", b); setB(data); toast.success("Bank details saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); } };
  const T = ({ k, label, ph }) => (<div><Label className="text-xs text-slate-400">{label}</Label><Input className={F} placeholder={ph} value={b[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`bank-${k}`} /></div>);
  return (
    <div className="max-w-3xl space-y-5">
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200">
        These bank details are shown on <b>invoices / manual-payment instructions</b> only. They are <b>NOT</b> your Razorpay settlement account — that is managed inside your Razorpay Merchant Dashboard (KYC/Settlement) and changing it there does not require any website change.
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <T k="account_holder" label="Account holder name" />
        <T k="bank_name" label="Bank name" />
        <div><Label className="text-xs text-slate-400">Account number {b.account_number_set && <span className="text-teal-400">(stored, masked)</span>}</Label><Input className={F} placeholder="Enter to set / replace" value={b.account_number ?? ""} onChange={(e) => set("account_number", e.target.value)} data-testid="bank-account_number" /></div>
        <T k="ifsc" label="IFSC" /><T k="branch" label="Branch" />
        <T k="account_type" label="Account type (Current/Savings)" />
        <T k="upi_id" label="UPI ID" /><T k="upi_name" label="UPI display name" />
        <div className="sm:col-span-2"><T k="notes" label="Notes" /></div>
      </div>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={!!b.active} onCheckedChange={(v) => set("active", v)} data-testid="bank-active" /> Active</label>
        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={!!b.show_on_invoice} onCheckedChange={(v) => set("show_on_invoice", v)} data-testid="bank-show_on_invoice" /> Show on invoice</label>
      </div>
      <Button className="bg-teal hover:bg-teal-dark text-white" onClick={save} disabled={saving} data-testid="bank-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save bank details"}</Button>
    </div>
  );
}

export default function AdminBilling() {
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Billing & Invoices</h1>
      <Tabs defaultValue="invoices">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="invoices" data-testid="bill-tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="settings" data-testid="bill-tab-settings">Billing Settings</TabsTrigger>
          <TabsTrigger value="bank" data-testid="bill-tab-bank">Bank Details</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-4"><Invoices /></TabsContent>
        <TabsContent value="settings" className="mt-4"><BillingSettings /></TabsContent>
        <TabsContent value="bank" className="mt-4"><BankDetails /></TabsContent>
      </Tabs>
    </div>
  );
}
